import { useRef, useState, type DragEvent, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

import type { BadgeTone } from './Badge';
import { Icon } from './Icon';
import { encaixarColunas } from './kanbanLayout';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import { useLeituraDoQuadro } from './useLeituraDoQuadro';

export interface KanbanColumn {
  key: string;
  /** Nome da coluna, no cabecalho e para o leitor de tela. */
  label: string;
  /** Cor da coluna, com o mesmo sentido das etiquetas da plataforma. */
  tone?: BadgeTone | undefined;
}

export interface KanbanProps<T> {
  /** As colunas, na ordem do fluxo. */
  columns: KanbanColumn[];
  items: T[];
  keyOf: (item: T) => string;
  /** A coluna em que o item esta. */
  columnOf: (item: T) => string;
  /**
   * As colunas para onde o item pode ir, como o servidor informou. A coluna fora da
   * lista recusa a solta: o quadro nunca oferece movimento que o servidor negaria.
   */
  targetsOf: (item: T) => string[];
  /** Quem pode mover. Sem a permissao, o quadro so le. */
  canMove: boolean;
  onMove: (item: T, column: string) => void;
  /** Abrir o item, pelo clique ou pelo teclado. */
  onOpen: (item: T) => void;
  renderCard: (item: T) => ReactNode;
  loading?: boolean | undefined;
  loadingLabel?: string | undefined;
  /** Texto da coluna vazia, como "Nenhum corretor". */
  emptyLabel?: string | undefined;
  /** Texto da coluna vazia que aceita a solta. */
  dropLabel?: string | undefined;
  /** Rotulo da contagem para o leitor de tela. */
  countLabel?: ((count: number, column: KanbanColumn) => string) | undefined;
  /**
   * Total da coluna quando o quadro mostra so uma parte dela (paginacao por coluna,
   * como "9 / 99"). Sem ele, a contagem e a dos itens exibidos.
   */
  totalOf?: ((column: KanbanColumn) => number | undefined) | undefined;
  /** Rodape da coluna, como o "Ver mais" da coluna paginada. */
  footerOf?: ((column: KanbanColumn) => ReactNode) | undefined;
}

/**
 * A cor da coluna aparece na faixa de cima, no veu do cabecalho, no ponto antes do
 * nome e no contador. O nome e o numero continuam escritos: a cor so ajuda a achar.
 */
const coresDaColuna: Record<
  BadgeTone,
  { faixa: string; cabecalho: string; ponto: string; contador: string }
> = {
  neutral: {
    faixa: 'bg-ink-300',
    cabecalho: 'from-ink-50',
    ponto: 'bg-ink-400',
    contador: 'bg-ink-100 text-ink-700 ring-ink-200',
  },
  brand: {
    faixa: 'bg-brand-500',
    cabecalho: 'from-brand-50',
    ponto: 'bg-brand-500',
    contador: 'bg-brand-50 text-brand-700 ring-brand-200',
  },
  info: {
    faixa: 'bg-info-500',
    cabecalho: 'from-info-50',
    ponto: 'bg-info-500',
    contador: 'bg-info-50 text-info-700 ring-info-200',
  },
  success: {
    faixa: 'bg-success-500',
    cabecalho: 'from-success-50',
    ponto: 'bg-success-500',
    contador: 'bg-success-50 text-success-700 ring-success-200',
  },
  warning: {
    faixa: 'bg-warning-500',
    cabecalho: 'from-warning-50',
    ponto: 'bg-warning-500',
    contador: 'bg-warning-50 text-warning-700 ring-warning-200',
  },
  danger: {
    faixa: 'bg-danger-500',
    cabecalho: 'from-danger-50',
    ponto: 'bg-danger-500',
    contador: 'bg-danger-50 text-danger-700 ring-danger-200',
  },
};

/** Tipo proprio do arrasto: o cartao solto num campo da tela nao vira texto. */
const TIPO_DO_ARRASTO = 'application/x-seguracore-cartao';

/** Distancia da borda em que o arrasto leva o quadro para o lado. */
const BORDA_DE_ARRASTO = 64;
const PASSO_DE_ARRASTO = 18;

/** Colunas desenhadas enquanto a esteira ainda nao disse quantas sao. */
const COLUNAS_NO_CARREGAMENTO = 5;

/**
 * O quadro em colunas da plataforma. Toda tela com colunas e cartoes usa este
 * componente, para que todos os quadros tenham a mesma aparencia e o mesmo
 * comportamento.
 *
 * A tabela responde "qual e a situacao deste item". O quadro responde a pergunta que
 * a operacao faz todo dia: quantos estao parados em cada etapa e o que falta para
 * destravar.
 *
 * As colunas dividem a largura disponivel e vao ate o fim da janela (`kanbanLayout` e
 * `useLeituraDoQuadro`). A moldura solta a largura maxima da pagina quando encontra
 * `data-kanban`. Quando as colunas nao cabem nem no minimo legivel, o quadro rola com
 * sombra na borda e o aviso de quantas colunas estao na tela: nenhuma coluna fica
 * cortada sem sinal. A lista longa rola dentro da coluna, com o cabecalho parado.
 *
 * O movimento acontece arrastando o cartao, para os destinos que o servidor informou.
 * O cartao e um botao de verdade: quem navega pelo teclado abre o item e move pela
 * tela de detalhe, que oferece as mesmas etapas. O estado do arrasto e local: um
 * arrasto interrompido nao deixa rastro em lugar nenhum.
 */
export function Kanban<T>({
  columns,
  items,
  keyOf,
  columnOf,
  targetsOf,
  canMove,
  onMove,
  onOpen,
  renderCard,
  loading,
  loadingLabel = 'Carregando',
  emptyLabel = 'Nenhum item',
  dropLabel = 'Solte aqui',
  countLabel = (count, column) => `${count} em ${column.label}`,
  totalOf,
  footerOf,
}: KanbanProps<T>) {
  const [arrastando, setArrastando] = useState<T | null>(null);
  const [alvo, setAlvo] = useState<string | null>(null);

  const area = useRef<HTMLDivElement>(null);
  const leitura = useLeituraDoQuadro(area);

  const quantidade = Math.max(
    columns.length || (loading ? COLUNAS_NO_CARREGAMENTO : 0),
    1,
  );
  const encaixe = encaixarColunas(leitura.largura, quantidade);
  const transborda = !loading && (leitura.antes || leitura.depois);

  function aceita(coluna: string): boolean {
    if (arrastando === null || !canMove) return false;
    return targetsOf(arrastando).includes(coluna);
  }

  function soltar(event: DragEvent<HTMLDivElement>, coluna: string) {
    event.preventDefault();
    setAlvo(null);
    const item = arrastando;
    setArrastando(null);
    // A permissao e conferida de novo na solta: o arrasto pode vir de fora do quadro.
    if (
      canMove &&
      item !== null &&
      columnOf(item) !== coluna &&
      targetsOf(item).includes(coluna)
    ) {
      onMove(item, coluna);
    }
  }

  /** O destino pode estar fora de vista: perto da borda, o arrasto leva o quadro. */
  function rolarNaBorda(event: DragEvent<HTMLDivElement>) {
    if (arrastando === null) return;
    const elemento = event.currentTarget;
    const caixa = elemento.getBoundingClientRect();
    if (event.clientX > caixa.right - BORDA_DE_ARRASTO) {
      elemento.scrollLeft += PASSO_DE_ARRASTO;
    } else if (event.clientX < caixa.left + BORDA_DE_ARRASTO) {
      elemento.scrollLeft -= PASSO_DE_ARRASTO;
    }
  }

  function passarColuna(sentido: 1 | -1) {
    const elemento = area.current;
    const coluna = elemento?.firstElementChild as HTMLElement | null | undefined;
    if (!elemento || !coluna) return;
    const reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    elemento.scrollBy({
      left: sentido * (coluna.offsetWidth + encaixe.espaco),
      behavior: reduzido ? 'auto' : 'smooth',
    });
  }

  return (
    <div data-kanban className="flex min-w-0 flex-col gap-2">
      {transborda ? (
        <div
          data-kanban-aviso
          className="flex items-center justify-end gap-2 text-2xs text-ink-500"
        >
          <span className="tabular-nums">
            {leitura.primeira === leitura.ultima
              ? `Coluna ${leitura.primeira} de ${columns.length}`
              : `Colunas ${leitura.primeira} a ${leitura.ultima} de ${columns.length}`}
          </span>
          <BotaoDeColuna
            icone="chevronLeft"
            rotulo="Ver colunas anteriores"
            disabled={!leitura.antes}
            onClick={() => passarColuna(-1)}
          />
          <BotaoDeColuna
            icone="chevronRight"
            rotulo="Ver proximas colunas"
            disabled={!leitura.depois}
            onClick={() => passarColuna(1)}
          />
        </div>
      ) : null}

      <div className="relative min-w-0">
        <SombraDaBorda lado="esquerda" visivel={transborda && leitura.antes} />
        <SombraDaBorda lado="direita" visivel={transborda && leitura.depois} />

        {loading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <Spinner size={24} label={loadingLabel} className="text-brand-500" />
          </div>
        ) : null}

        <div
          ref={area}
          onDragOver={rolarNaBorda}
          style={{
            gridTemplateColumns: `repeat(${quantidade}, minmax(${encaixe.coluna}px, 1fr))`,
            columnGap: encaixe.espaco,
          }}
          className={cn(
            'rolagem-fina grid grid-rows-[minmax(0,1fr)] overflow-x-auto overflow-y-hidden',
            transborda ? 'pb-2.5' : 'pb-0.5',
            // O passo do arrasto e pequeno: com o encaixe ligado, o quadro voltaria
            // para a coluna de onde saiu a cada passo.
            transborda && arrastando === null && 'snap-x snap-proximity',
          )}
        >
          {loading
            ? Array.from({ length: quantidade }, (_, indice) => (
                <ColunaCarregando key={indice} />
              ))
            : columns.map((coluna) => {
                const daColuna = items.filter((item) => columnOf(item) === coluna.key);
                const destino = aceita(coluna.key);
                const emDestaque = destino && alvo === coluna.key;
                const apagada =
                  arrastando !== null &&
                  !destino &&
                  columnOf(arrastando) !== coluna.key;
                const total = totalOf?.(coluna);
                const parcial = total !== undefined && total !== daColuna.length;
                const cores = coresDaColuna[coluna.tone ?? 'neutral'];
                const rodape = footerOf?.(coluna);

                return (
                  <div
                    key={coluna.key}
                    role="group"
                    aria-label={coluna.label}
                    onDragOver={(event) => {
                      if (!destino) return;
                      // Sem impedir o padrao o navegador nao permite a solta.
                      event.preventDefault();
                      setAlvo(coluna.key);
                    }}
                    onDragLeave={(event) => {
                      // Passar por cima de um cartao da coluna nao e sair dela.
                      if (event.currentTarget.contains(event.relatedTarget as Node)) {
                        return;
                      }
                      setAlvo((atual) => (atual === coluna.key ? null : atual));
                    }}
                    onDrop={(event) => soltar(event, coluna.key)}
                    className={cn(
                      '@container flex min-h-0 min-w-0 snap-start flex-col overflow-hidden',
                      'rounded-xl border bg-surface-sunken shadow-(--shadow-xs)',
                      'transition-[border-color,background-color,box-shadow,opacity]',
                      'duration-(--duration-fast)',
                      emDestaque
                        ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-500/25'
                        : destino
                          ? 'border-dashed border-brand-400'
                          : 'border-line',
                      apagada && 'opacity-55',
                    )}
                  >
                    <div aria-hidden className={cn('h-1 shrink-0', cores.faixa)} />

                    <div
                      className={cn(
                        'flex min-h-11 shrink-0 items-center justify-between gap-1.5 border-b border-line',
                        'bg-linear-to-b to-surface px-2 py-1.5 @min-[10rem]:px-2.5',
                        cores.cabecalho,
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span
                          aria-hidden
                          className={cn(
                            'hidden size-2 shrink-0 rounded-full @min-[9rem]:block',
                            cores.ponto,
                          )}
                        />
                        <span
                          title={coluna.label}
                          className="line-clamp-2 min-w-0 text-xs leading-tight font-semibold wrap-anywhere text-ink-900"
                        >
                          {coluna.label}
                        </span>
                      </span>
                      <span
                        aria-label={countLabel(total ?? daColuna.length, coluna)}
                        title={parcial ? `${daColuna.length} / ${total}` : undefined}
                        className={cn(
                          'shrink-0 rounded-full px-1.5 py-0.5 text-2xs leading-none',
                          'font-semibold whitespace-nowrap tabular-nums ring-1 ring-inset',
                          cores.contador,
                        )}
                      >
                        {parcial ? (
                          // Na coluna estreita o parcial cortaria o nome da coluna. Ele
                          // continua no rodape da coluna paginada e no titulo.
                          <>
                            <span className="@max-[11rem]:hidden">
                              {daColuna.length} /{' '}
                            </span>
                            {total}
                          </>
                        ) : (
                          daColuna.length
                        )}
                      </span>
                    </div>

                    <div className="rolagem-fina flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-1 @min-[10rem]:p-1.5 @min-[12rem]:p-2">
                      {daColuna.map((item) => {
                        const arrastavel = canMove && targetsOf(item).length > 0;
                        return (
                          <button
                            key={keyOf(item)}
                            type="button"
                            draggable={arrastavel}
                            onDragStart={(event) => {
                              if (!arrastavel) return;
                              // O Firefox so comeca o arrasto com um dado no transporte.
                              event.dataTransfer.setData(TIPO_DO_ARRASTO, keyOf(item));
                              event.dataTransfer.effectAllowed = 'move';
                              setArrastando(item);
                            }}
                            onDragEnd={() => {
                              setArrastando(null);
                              setAlvo(null);
                            }}
                            onClick={() => onOpen(item)}
                            className={cn(
                              'relative w-full shrink-0 rounded-lg border border-line bg-surface p-1.5 text-left',
                              '@min-[10rem]:p-2 @min-[12rem]:p-2.5',
                              'shadow-(--shadow-xs) transition-[border-color,box-shadow,transform,opacity]',
                              'duration-(--duration-fast) ease-(--ease-out-soft)',
                              'hover:-translate-y-px hover:border-brand-300 hover:shadow-(--shadow-md)',
                              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-500',
                              arrastavel
                                ? 'cursor-grab active:cursor-grabbing'
                                : 'cursor-pointer',
                              arrastando === item && 'opacity-50 shadow-none',
                            )}
                          >
                            {renderCard(item)}
                          </button>
                        );
                      })}

                      {daColuna.length === 0 ? (
                        <div
                          className={cn(
                            'flex min-h-24 flex-1 flex-col items-center justify-center gap-1.5',
                            'rounded-lg border border-dashed px-2 py-6 text-center text-2xs',
                            destino
                              ? 'border-brand-300 font-medium text-brand-700'
                              : 'border-line-strong text-ink-500',
                          )}
                        >
                          <Icon name="layers" size={16} className="text-ink-300" />
                          <p>{destino ? dropLabel : emptyLabel}</p>
                        </div>
                      ) : null}
                    </div>

                    {rodape ? (
                      <div className="shrink-0 border-t border-line bg-surface p-1">
                        {rodape}
                      </div>
                    ) : null}
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
}

/** Sombra na borda do quadro: diz que ha mais colunas sem cobrir a coluna. */
function SombraDaBorda({
  lado,
  visivel,
}: {
  lado: 'esquerda' | 'direita';
  visivel: boolean;
}) {
  return (
    <div
      aria-hidden
      className={cn(
        // Para no fim das colunas: a folga e a barra de rolagem ficam de fora.
        'pointer-events-none absolute top-0 bottom-4.5 z-10 w-10 from-ink-900/15 to-transparent',
        'transition-opacity duration-(--duration-base)',
        lado === 'esquerda'
          ? 'left-0 rounded-l-lg bg-linear-to-r'
          : 'right-0 rounded-r-lg bg-linear-to-l',
        visivel ? 'opacity-100' : 'opacity-0',
      )}
    />
  );
}

function BotaoDeColuna({
  icone,
  rotulo,
  disabled,
  onClick,
}: {
  icone: 'chevronLeft' | 'chevronRight';
  rotulo: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      title={rotulo}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex size-6 items-center justify-center rounded-md border border-line bg-surface',
        'text-ink-600 shadow-(--shadow-xs) transition-colors duration-(--duration-fast)',
        'hover:border-line-strong hover:bg-ink-50 hover:text-ink-900',
        'disabled:cursor-default disabled:opacity-40 disabled:shadow-none',
        'disabled:hover:border-line disabled:hover:bg-surface disabled:hover:text-ink-600',
      )}
    >
      <Icon name={icone} size={14} />
    </button>
  );
}

function ColunaCarregando() {
  return (
    <div
      aria-hidden
      className="flex min-h-0 min-w-0 flex-col gap-2 overflow-hidden rounded-xl border border-line bg-surface-sunken p-2"
    >
      <Skeleton className="h-7 w-2/3 shrink-0" />
      <Skeleton className="h-24 w-full shrink-0" />
      <Skeleton className="h-24 w-full shrink-0" />
    </div>
  );
}
